import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9sm0458o1vn02ekuhng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于蜜桃视频 - 品牌介绍 | 蜜桃视频官网"
        description="了解蜜桃视频的品牌故事、发展历程、用户口碑和联系方式。蜜桃视频致力于为用户提供高品质视频观看体验，月活跃用户突破5000万。"
        keywords="蜜桃视频,关于蜜桃视频,蜜桃视频品牌,蜜桃视频联系方式,蜜桃视频口碑"
        canonicalUrl="https://www.mitaovideo.com/about"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蜜桃视频',
            description: '了解蜜桃视频的品牌故事和发展历程。',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '蜜桃视频主页', item: 'https://www.mitaovideo.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.mitaovideo.com/about' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: aboutData.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.q,
              acceptedAnswer: { '@type': 'Answer', text: faq.a },
            })),
          },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-10 md:pb-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '蜜桃视频主页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {aboutData.brandIntro.title}
              </h1>
              <p className="mt-2 text-foreground-500 text-sm">
                {aboutData.brandIntro.subtitle}
              </p>
            </div>

            {/* Brand Intro */}
            <section className="mt-10 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground-900 font-heading mb-4">
                    品牌故事
                  </h2>
                  {aboutData.brandIntro.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-sm text-foreground-600 leading-relaxed mb-3">
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div className="lg:w-[380px] shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20office%20interior%20with%20warm%20peach%20and%20coral%20toned%20decor%2C%20creative%20workspace%2C%20media%20company%20headquarters%2C%20bright%20natural%20lighting%2C%20clean%20minimalist%20aesthetic%2C%20collaborative%20environment%2C%20professional%20atmosphere&width=760&height=500&seq=about-brand-01&orientation=landscape"
                    alt="蜜桃视频办公环境"
                    className="w-full rounded-lg object-cover object-top"
                  />
                </div>
              </div>
            </section>

            {/* Milestones */}
            <section className="mt-8 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <h2 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-[19px] top-3 bottom-3 w-0.5 bg-accent-200"></div>
                <div className="flex flex-col gap-5">
                  {aboutData.milestones.map((milestone, index) => (
                    <div key={index} className="flex items-start gap-5 relative">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0 z-10">
                        <span className="text-xs font-bold text-accent-700">{milestone.year.slice(2)}</span>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-foreground-900">{milestone.year}年</span>
                        <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mt-8 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  {aboutData.reputation.title}
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  {aboutData.reputation.subtitle}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutData.reputation.reviews.map((review, index) => (
                  <div key={index} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-accent-700">
                          {review.name[0]}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                        <div className="text-xs text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                    <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-8 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  常见问题
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  关于蜜桃视频的常见疑问
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {aboutData.faqs.map((faq, index) => (
                  <div key={index} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                      <a id={`faq-${index}`} className="no-underline">{faq.q}</a>
                    </h4>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mt-8 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <div className="text-center mb-8">
                <h2 className="text-xl font-bold text-foreground-900 font-heading">
                  {aboutData.contact.title}
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  {aboutData.contact.subtitle}
                </p>
              </div>
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-phone-line', label: '客服热线', value: aboutData.contact.phone },
                    { icon: 'ri-mail-line', label: '电子邮箱', value: aboutData.contact.email },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: aboutData.contact.address },
                    { icon: 'ri-time-line', label: '工作时间', value: aboutData.contact.workingHours },
                    { icon: 'ri-wechat-line', label: '微信公众号', value: aboutData.contact.wechat },
                    { icon: 'ri-weibo-line', label: '官方微博', value: aboutData.contact.weibo },
                  ].map((item, index) => (
                    <div key={index} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
                          <i className={`${item.icon} text-primary-500`}></i>
                        </div>
                        <div>
                          <div className="text-xs text-foreground-500">{item.label}</div>
                          <div className="text-sm font-medium text-foreground-900 mt-0.5">{item.value}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lg:w-[360px] shrink-0">
                  <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                    <form id="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-3" data-readdy-form>
                      <input type="text" name="name" placeholder="您的姓名" required className="w-full px-3 py-2 text-sm rounded-md bg-background-50 border border-background-200/70 focus:border-primary-300 focus:outline-none text-foreground-900 placeholder:text-foreground-400" />
                      <input type="email" name="email" placeholder="您的邮箱" required className="w-full px-3 py-2 text-sm rounded-md bg-background-50 border border-background-200/70 focus:border-primary-300 focus:outline-none text-foreground-900 placeholder:text-foreground-400" />
                      <textarea name="message" placeholder="您的留言（500字以内）" required maxLength={500} rows={4} className="w-full px-3 py-2 text-sm rounded-md bg-background-50 border border-background-200/70 focus:border-primary-300 focus:outline-none text-foreground-900 placeholder:text-foreground-400 resize-none"></textarea>
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="frm-anti-spam" />
                      <button type="submit" className="w-full px-5 py-2.5 text-sm font-medium text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
                        发送消息
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-sm text-green-600 text-center">消息已发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 text-center">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}